import { IconAlertCircle } from '@developerz/ui';
import { createSignal, type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { isForbidden, isUnauthorized } from '../lib/api';
import { pickKeyOutageKey, useKeyOutage } from '../lib/key-outage';
import { createMutation } from '../lib/mutation';
import { KeyOutageNotice } from './KeyOutageNotice';
import { Button } from './ui/Button';

// Container for the account-wide key-outage banner (epic #5099, slice #5119).
// Reads `useKeyOutage` and owns the pick through `createMutation`, with
// `onSettled: () => query.refetch()` — the server is the arbiter of whether
// the outage closed after the pick, never a patch from the echo.
//
// `role="status"` (not `role="alert"`) on the rendered notice: a banner that
// exists only because the server has new work for the maintainer is an
// announcement, not an emergency — the assertive role would steal focus from
// the page the maintainer is on for a "next time you look" notice.
//
// Not a `<QueryBoundary/>`: the banner exists only when there IS an outage,
// and a closed outage (`outage: null`) must paint NOTHING — no skeleton, no
// empty line. Mirrors `EmailBounceAlert`'s reason for not using one. The
// 403/401 arms render nothing at all: a viewer who legitimately cannot see
// the outage would otherwise get a denial copy on every page.

export function KeyOutageBanner(props: { token: string | null }): JSX.Element {
  const query = useKeyOutage(props.token);
  // The picked label is OWNED HERE, not in the notice: the submit button's
  // `disabled` (no label chosen yet) is a parent concern, and the
  // local signal is what the form actually submits. Cleared on settle so a
  // second outage (if the first pick is refused) starts with no stale pick.
  const [pickedLabel, setPickedLabel] = createSignal<string | null>(null);

  const pick = createMutation({
    call: (keyLabel: string) => pickKeyOutageKey(keyLabel, props.token),
    fallback: t('key_outage.pick_error'),
    // RETURNED, not fired-and-forgotten: `createMutation` awaits `onSettled`
    // inside the pending window (`lib/mutation.ts`), so the picker's control
    // stays disabled until the post-pick re-read has actually landed, not
    // just been kicked off.
    onSettled: () => {
      setPickedLabel(null);
      return query.refetch();
    },
  });

  return (
    <Switch>
      <Match when={query.isError && (isForbidden(query.error) || isUnauthorized(query.error))}>
        {/* Render NOTHING. A denial would put copy on every page for every
            viewer without `read:billing`; a 401 means the session died and the
            app gate already handles that. */}
        {null}
      </Match>
      <Match when={query.isError}>
        {/* A failed read (5xx, `ApiSchemaError`) must not look like "no
            outage" — but a full `<ErrorState/>` (`role="alert"`, generic
            title) is wrong here too: this is a low-urgency "next time you
            look" banner, not a page failure. One compact line, same
            `role="status"` the notice itself renders, scoped copy, and a
            Retry that re-reads. */}
        <div class="alert alert--icon" role="status">
          <IconAlertCircle />
          <p class="alert__title">{t('key_outage.read_error')}</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('errors.retry')}
          </Button>
        </div>
      </Match>
      <Match when={query.data?.outage}>
        {(outage) => (
          <KeyOutageNotice
            outage={outage()}
            pickedLabel={pickedLabel()}
            picking={pick.pending()}
            pickError={pick.error()}
            onChange={setPickedLabel}
            onSubmit={(label) => void pick.run(label)}
          />
        )}
      </Match>
    </Switch>
  );
}
